import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SignInButton, useUser } from "@clerk/clerk-react";
import {
  CheckIcon,
  StarIcon,
  ChatBubbleLeftRightIcon,
  BoltIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  Bars3Icon,
  XMarkIcon,
  PlayIcon,
  SparklesIcon,
  RocketLaunchIcon,
  LockClosedIcon,
  ClockIcon,
  ArrowRightIcon,
  ChatBubbleBottomCenterTextIcon,
  UserIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import {
  CheckIcon as CheckIconSolid,
  StarIcon as StarIconSolid,
  SparklesIcon as SparklesIconSolid,
} from "@heroicons/react/24/solid";
import {
  Zap,
  MessageSquare,
  BarChart3,
  Shield,
  Plus,
  Check,
  Clock,
  ArrowRight,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import FAQWidgetComponent from "./FAQWidgetComponent";
import ArchitectureIcon from '@mui/icons-material/Architecture';
import ChatWidgetComponent from "./ChatWidgetComponent";

// FAQ Accordion Component
const FAQAccordion = ({ faqs, className = "" }) => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <div></div>{" "}
      {faqs.map((faq, index) => (
        <div
          key={index}
          className={`bg-gradient-to-r from-blue-500/5 via-indigo-500/5 to-purple-500/5 backdrop-blur-sm rounded-xl border border-gray-200/60 transition-all duration-300 hover:border-gray-300 ${openIndex === index
            ? "shadow-lg border-blue-200 bg-gradient-to-r from-blue-50/60 via-indigo-50/40 to-purple-50/60"
            : "hover:shadow-md hover:from-white/98 hover:via-blue-50/40 hover:to-purple-50/40"
            }`}
        >
          <button
            onClick={() => toggleFAQ(index)}
            className={`w-full p-4 lg:p-5 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500/20 rounded-xl group transition-all duration-300 ${openIndex === index
              ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md"
              : "bg-gradient-to-r from-gray-100 to-gray-50 text-gray-600 group-hover:from-blue-100 group-hover:to-purple-100 group-hover:text-blue-600"
              }`}
            aria-expanded={openIndex === index}
            aria-controls={`faq-answer-${index}`}
          >
            <span
              className={`text-gray-800 text-sm lg:text-base transition-all duration-200 ${openIndex === index
                ? "font-semibold text-blue-900"
                : "font-medium group-hover:text-gray-900"
                }`}
            >
              {faq.question}
            </span>
            <div
              className={`flex-shrink-0 ml-4 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${openIndex === index
                ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md"
                : "bg-gradient-to-r from-gray-100 to-gray-50 text-gray-600 group-hover:from-blue-100 group-hover:to-purple-100 group-hover:text-blue-600"
                }`}
            >
              {openIndex === index ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </div>
          </button>{" "}
          <div
            id={`faq-answer-${index}`}
            className={`overflow-hidden faq-content transition-all duration-500 ease-in-out ${openIndex === index ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
              }`}
          >
            <div className="px-4 lg:px-5 pb-4 lg:pb-5">
              <div
                className={`border-t border-gray-100/60 pt-4 ${openIndex === index ? "animate-fadeInUp" : ""
                  }`}
              >
                <div className="bg-gradient-to-r from-blue-50/80 via-indigo-50/60 to-purple-50/80 backdrop-blur-sm rounded-lg p-4 border border-blue-100/50 shadow-inner">
                  <p className="text-gray-700 text-sm lg:text-base leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

function LandingPage() {
  const { user, isSignedIn } = useUser();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [lineHeight, setLineHeight] = useState(0);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  // Animation state for badge text
  const [currentOptimizationIndex, setCurrentOptimizationIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationPhase, setAnimationPhase] = useState("idle"); // idle, fadeOut, fadeIn
   const [currency, setCurrency] = useState("USD");
   const currencyRates = {
  USD: 1,
  EUR: 0.92,
  INR: 83.2,
  GBP: 0.79,
};

const currencySymbols = {
  USD: "$",
  EUR: "€",
  INR: "₹",
  GBP: "£",
};

  const convertPrice = (price) => {
    if (price === "Custom") return price;
    const converted = (price * currencyRates[currency]).toFixed(2);
    return `${currencySymbols[currency]}${converted}`;
  };

  // If user signs in via the modal, redirect to billing when requested
  useEffect(() => {
    try {
      const redirectAfter = localStorage.getItem('redirectAfterSignIn');
      if (isSignedIn && redirectAfter) {
        localStorage.removeItem('redirectAfterSignIn');
        navigate(redirectAfter);
      }
    } catch (err) {
      console.warn('redirectAfterSignIn check failed', err);
    }
  }, [isSignedIn, navigate]);

  const optimizationTexts = [
    "Built For Search Engine Optimization",
    "Built For Answer Engine Optimization",
    "Built For Generative Engine Optimization",
  ];

  // Refs for stepper steps
  const stepperSectionRef = useRef(null);
  const step1Ref = useRef(null);
  const step2Ref = useRef(null);
  const step3Ref = useRef(null);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []); // Scroll-based line animation for stepper
  useEffect(() => {
    const handleStepperScroll = () => {
      if (!stepperSectionRef.current) return;

      const stepperRect = stepperSectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const stepperTop = stepperRect.top;
      const stepperHeight = stepperRect.height;

      // Calculate scroll progress through the stepper section
      let scrollProgress = 0;

      // When section starts entering viewport from bottom
      if (stepperTop <= windowHeight && stepperTop + stepperHeight >= 0) {
        // Section is visible
        if (stepperTop <= windowHeight * 0.7) {
          // Start animating when section is 70% into viewport
          const entryProgress =
            (windowHeight * 0.7 - stepperTop) /
            (stepperHeight + windowHeight * 0.3);
          scrollProgress = Math.min(1, Math.max(0, entryProgress));
        }
      }

      // Smooth easing function for more natural animation
      const easeInOutCubic = (t) => {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      };

      const easedProgress = easeInOutCubic(scrollProgress);

      // Set line height starting from 15% and growing to 100%
      // 15% base + 85% animated = 100% total
      const baseHeight = 25;
      const animatedHeight = 85;
      setLineHeight(baseHeight + easedProgress * animatedHeight);
    };

    // Throttle scroll events for better performance
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleStepperScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", throttledScroll);
    window.addEventListener("resize", handleStepperScroll);

    // Initial calculation
    handleStepperScroll();
    return () => {
      window.removeEventListener("scroll", throttledScroll);
      window.removeEventListener("resize", handleStepperScroll);
    };
  }, []);
  // Badge text animation useEffect
  useEffect(() => {
    const interval = setInterval(() => {
      // Start fade out animation
      setAnimationPhase("fadeOut");
      setIsAnimating(true);

      // After fade out completes, change text and start fade in
      setTimeout(() => {
        setCurrentOptimizationIndex(
          (prevIndex) => (prevIndex + 1) % optimizationTexts.length
        );
        setAnimationPhase("fadeIn");

        // Complete the animation cycle
        setTimeout(() => {
          setAnimationPhase("idle");
          setIsAnimating(false);
        }, 400); // Fade in duration
      }, 400); // Fade out duration
    }, 4000); // Change text every 4 seconds (increased for better readability)

    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      title: "AI-Powered FAQ Generation",
      description:
        "Automatically generate FAQs from website content, support docs, or customer interactions in seconds.",
      mockupType: "faq",
      gradient: "from-purple-500 via-pink-500 to-orange-400",
    },
    {
      title: "Smart Chat Support",
      description:
        "Context-aware chatbot that provides accurate answers based on your knowledge base and learns from every interaction.",
      mockupType: "chat",
      gradient: "from-blue-500 via-purple-500 to-pink-400",
    },
    {
      title: "SEO Optimization",
      description:
        "Built-in FAQ schema markup for better search visibility and AI answer optimization to boost your rankings.",
      mockupType: "seo",
      gradient: "from-indigo-500 via-purple-500 to-pink-500",
    },
  ];

  // Custom function to handle trial button click
  const handleStartTrial = () => {
    if (isSignedIn) {
      // User is already signed in, navigate to billing
      navigate('/billing');
    } else {
      // User not signed in, handle with SignInButton
      return false; // This will allow the SignInButton to handle the click
    }
  };

  const pricing = [
    {
      name: "Starter",
      price: 99,
      description: "Great for small teams getting started with AI support",
      trial: "14-day free trial",
      features: [
        "Files: Text + PDF + DOCX (20 files, 20MB)",
        "Emails: 100 per month",
        "Tokens: 100,000 per month",
        "Regional Language: Auto Learning Mode",
        "AI Agent: BASIC",
        "FAQ Pages: Unlimited",
        "Widget Views: 10,000 per month",
        "Export/Backup: NA",
        "Support: Email support",
      ],
      cta: "Start Free Trial",
      popular: false,
    },
    {
      name: "Professional",
      price: 599,
      description: "For growing businesses that need more scale and insights",
      trial: "14-day free trial",
      features: [
        "Files: All types (200 files, 100MB storage)",
        "Emails: 500 per month",
        "Tokens: 500,000 per month",
        "AI Agent: Custom training",
        "FAQ Pages: Unlimited",
        "Widget Views: 50,000 per month",
        "Export/Backup: Export as csv",
        "Support: Priority email + phone",
        "Regional Language: Auto Learning Mode",
        "Analytics: Advanced dashboard + reporting (coming soon)",
      ],
      cta: "Start Free Trial",
      popular: true,
    },
    {
      name: "Enterprise",
      price: "1499",
      description: "Enterprise-grade package with dedicated services and security",
      trial: "14-day free trial",
      features: [
        "Files: Unlimited (299MB+ storage)",
        "Emails: 2,000 per month",
        "Tokens: 20,000,000 per month",
        "AI Agent: Custom training + prompts",
        "FAQ Pages: Unlimited pages + SEO optimization",
        "Widget Views: 200,000 per month",
        "Export/Backup: Enterprise security + compliance",
        "Support: Dedicated account manager + phone",
        "Regional Language: Auto Learning Mode",
        "Features: Team collaboration + SSO + custom integrations",
      ],
      cta: "Start Free Trial",
      popular: false,
    },
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Head of Customer Success",
      company: "TechFlow Solutions",
      content:
        "AEO Platform reduced our support tickets by 60% and improved customer satisfaction scores dramatically. The AI responses are incredibly accurate.",
      rating: 5,
    },
    {
      name: "Marcus Rodriguez",
      role: "Founder & CEO",
      company: "StartupBoost",
      content:
        "Implementation took less than 30 minutes. Our customers love the instant, accurate answers. ROI was clear within the first month.",
      rating: 5,
    },
    {
      name: "Emily Watson",
      role: "Operations Director",
      company: "GrowthCorp",
      content:
        "The SEO benefits alone justified the cost. Our FAQ pages now rank higher and drive more organic traffic. Plus, the time savings are incredible.",
      rating: 5,
    },
  ];

  // Carousel state for mobile logos
  const logoCarouselRef = useRef(null);
  useEffect(() => {
    // Only auto-scroll on mobile
    const isMobile = window.innerWidth < 768;
    if (!isMobile || !logoCarouselRef.current) return;
    let scrollAmount = 0;
    const scrollStep = 1; // px per frame
    const scrollDelay = 30; // ms per frame
    let intervalId;
    const scrollLogos = () => {
      if (!logoCarouselRef.current) return;
      logoCarouselRef.current.scrollLeft += scrollStep;
      // Loop back to start for infinite effect
      if (
        logoCarouselRef.current.scrollLeft + logoCarouselRef.current.offsetWidth >=
        logoCarouselRef.current.scrollWidth
      ) {
        logoCarouselRef.current.scrollLeft = 0;
      }
    };
    intervalId = setInterval(scrollLogos, scrollDelay);
    return () => clearInterval(intervalId);
  }, []);

  return (
    <div className="bg-white">
      {/* Video Modal */}
      {isVideoModalOpen && (
        <div className="fixed inset-0 z-[999] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setIsVideoModalOpen(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <div className="mt-2">
                      <div className="relative aspect-video">
                         <video
                          className="w-full h-full rounded-lg"
                          controls
                          autoPlay
                          onError={(e) => {
                            console.error('Error loading video:', e);
                          }}
                        >
                          <source src="/watch-demo.mp4" type="video/mp4" />
                          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
                            <p className="text-gray-600">Unable to load video. Please try again later.</p>
                          </div>
                        </video>
                       
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setIsVideoModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Demo Video Modal */}
      {/* Navigation */}
      <nav
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled
          ? "bg-white/80 backdrop-blur-lg shadow-lg"
          : "bg-transparent"
          }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <SparklesIconSolid className="h-5 w-5 text-white" />
                </div>
                <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Uttik
                </span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a
                href="#about"
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                About
              </a>
              <a
                href="#features"
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                Features
              </a>
              <a
                href="#pricing"
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                Pricing
              </a>
              <a
                href="#testimonials"
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                Testimonials
              </a>
              {isSignedIn ? (
                <button 
                  onClick={() => navigate('/')}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2.5 rounded-full hover:shadow-lg hover:scale-105 transition-all duration-200 font-medium"
                >
                  Get Started
                </button>
              ) : (
                <SignInButton mode="modal" redirectUrl="/">
                  <button onClick={() => localStorage.setItem('redirectAfterSignIn','/')} className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2.5 rounded-full hover:shadow-lg hover:scale-105 transition-all duration-200 font-medium">
                    Get Started
                  </button>
                </SignInButton>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-600 hover:text-gray-900 p-2"
              >
                {mobileMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 py-4 bg-white/95 backdrop-blur-lg">
              <div className="flex flex-col space-y-4">
                <a
                  href="#about"
                  className="text-gray-600 hover:text-gray-900 px-4"
                >
                  About
                </a>
                <a
                  href="#features"
                  className="text-gray-600 hover:text-gray-900 px-4"
                >
                  Features
                </a>
                <a
                  href="#pricing"
                  className="text-gray-600 hover:text-gray-900 px-4"
                >
                  Pricing
                </a>
                <a
                  href="#testimonials"
                  className="text-gray-600 hover:text-gray-900 px-4"
                >
                  Testimonials
                </a>
                {isSignedIn ? (
                  <button 
                    onClick={() => navigate('/')}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2.5 rounded-full mx-4 font-medium"
                  >
                    Get Started
                  </button>
                ) : (
                  <SignInButton mode="modal" redirectUrl="/">
                    <button onClick={() => localStorage.setItem('redirectAfterSignIn','/')} className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2.5 rounded-full mx-4 font-medium">
                      Get Started
                    </button>
                  </SignInButton>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>{" "}
      {/* Hero Section */}
      <section className="relative min-h-screen overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-32 w-80 h-80 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
          <div className="absolute -bottom-40 -left-32 w-80 h-80 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute top-40 left-40 w-80 h-80 bg-indigo-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
          {/* Centered Hero Content */}
          <div className="flex flex-col items-center justify-center min-h-screen text-center space-y-12">
            {" "}
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/60 backdrop-blur-sm border border-purple-200 text-purple-700 text-sm font-medium overflow-hidden relative">
              <SparklesIcon
                className={`h-4 w-4 mr-2 transition-all duration-500 ease-out ${animationPhase === "fadeOut"
                  ? "rotate-180 scale-110"
                  : animationPhase === "fadeIn"
                    ? "rotate-0 scale-100"
                    : "rotate-0 scale-100"
                  }`}
              />
              <div className="relative">
                <span
                  className={`block transition-all duration-400 ease-in-out ${animationPhase === "fadeOut"
                    ? "opacity-0 transform translate-y-3 scale-95"
                    : animationPhase === "fadeIn"
                      ? "opacity-100 transform translate-y-0 scale-100"
                      : "opacity-100 transform translate-y-0 scale-100"
                    }`}
                  style={{
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  {optimizationTexts[currentOptimizationIndex]}
                </span>
                {/* Shimmer effect during animation */}
                <div
                  className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 transition-transform duration-1000 ${animationPhase === "fadeIn"
                    ? "translate-x-full"
                    : "-translate-x-full"
                    }`}
                />
              </div>
            </div>{" "}
            {/* Trusted By Section */}

            {/* Main Headline - Centered */}
            <h1 className="text-5xl md:text-7xl lg:text-7xl font-bold text-gray-900 leading-tight max-w-6xl">
              <span className=" bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-2 pb-1">
                The Answer Engine 
              </span>
              <br />
              <span className="block mt-2">That Builds Trust</span>
            </h1>
            {/* Subtitle */}
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Instant, trusted answers that guide buying decisions,
              <span className="font-semibold text-gray-800">
                {" "}
                reduce support load, and grow lifetime customer value.
              </span>{" "}
             
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {isSignedIn ? (
                <button 
                  onClick={() => navigate('/')}
                  className="group bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center"
                >
                  Start Free Trial
                  <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </button>
              ) : (
                <SignInButton mode="modal" navigate="/">
                  <button onClick={() => localStorage.setItem('redirectAfterSignIn','/')} className="group bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center">
                    Start Free Trial
                    <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </SignInButton>
              )}
              <button
                className="group border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white hover:shadow-lg transition-all duration-200 flex items-center justify-center"
                onClick={() => setIsVideoModalOpen(true)}
              >
                <PlayIcon className="mr-2 h-5 w-5" />
                Watch Demo
              </button>
            </div>{" "}
            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-gray-500">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Smarter than SEO</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>Quicker than chatbots</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span>Designed to turn search into sales—automatically</span>
              </div>
            </div>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center">
                <CheckIconSolid className="h-4 w-4 text-green-500 mr-1" />
                No credit card required
              </div>
              <div className="flex items-center">
                <CheckIconSolid className="h-4 w-4 text-green-500 mr-1" />
                14-day free trial
              </div>
              <div className="flex items-center">
                <CheckIconSolid className="h-4 w-4 text-green-500 mr-1" />
                Cancel anytime
              </div>
            </div>
            <div className="max-w-3xl mx-auto flex flex-col items-center w-full">
              <p className="text-base font-medium text-gray-700 mb-4 w-full text-center break-words">
                Trusted by leading brands worldwide to deliver exceptional customer support and drive business growth.
              </p>
              {/* Desktop: row, Mobile: carousel */}
              <div>
                {/* Desktop view */}
                <div className="hidden md:flex flex-row flex-nowrap justify-center items-center w-full py-2">
                  <div className="h-14 flex items-center justify-center mr-7">
                    <img
                      src="/gogetwell.png"
                      alt="GoGetWell"
                      className="max-h-9 w-auto object-contain align-middle"
                      style={{ marginTop: '11px'}}
                    />
                  </div>
                  <div className="h-14 flex items-center justify-center mr-5">
                    <img src="/Amazfit_logo_b.png" alt="Amazfit" className="max-h-11 w-auto object-contain align-middle" />
                  </div>
                  <div className="h-14 flex items-center justify-center mr-8">
                    <img src="/logo-honor-Photoroom.png" alt="HONOR" className="max-h-6 w-auto object-contain align-middle" />
                  </div>
                  <div className="h-14 flex items-center justify-center mr-3">
                    <img src="/doodledesk.png" alt="DoodleDesk" className="max-h-12 object-contain align-middle" />
                  </div>
                  <div className="h-14 flex items-center justify-center">
                    <img src="/DailyU.png" alt="DailyU" className="max-h-8 w-auto object-contain align-middle font-bold" />
                  </div>
                </div>
                {/* Mobile carousel view - pure CSS animation */}
                <div className="md:hidden w-full overflow-hidden py-4 relative">
                  <div className="logo-marquee flex flex-row items-center min-w-full" style={{width: 'max-content'}}>
                {/* Logos - duplicated for seamless loop */}
                    <div className="h-16 flex items-center justify-center min-w-[120px] mr-8">
                      <img src="/gogetwell.png" alt="GoGetWell" className="max-h-9 w-auto object-contain" style={{ marginTop: '10px'}}/>
                    </div>
                    <div className="h-16 flex items-center justify-center min-w-[120px] mr-6">
                      <img src="/Amazfit_logo_b.png" alt="Amazfit" className="max-h-11 w-auto object-contain" />
                    </div>
                    <div className="h-16 flex items-center justify-center min-w-[120px] mr-9">
                      <img src="/logo-honor-Photoroom.png" alt="HONOR" className="max-h-6 w-auto object-contain" />
                    </div>
                    <div className="h-16 flex items-center justify-center min-w-[120px] mr-0">
                      <img src="/doodledesk.png" alt="DoodleDesk" className="max-h-12 w-auto object-contain" />
                    </div>
                    <div className="h-16 flex items-center justify-center min-w-[120px]">
                      <img src="/DailyU.png" alt="DailyU" className="max-h-8 w-auto object-contain" />
                    </div>
                    {/* Duplicate for seamless loop */}
                    <div className="h-16 flex items-center justify-center min-w-[120px] mr-8">
                      <img src="/gogetwell.png" alt="GoGetWell" className="max-h-9 w-auto object-contain" style={{ marginTop: '10px'}} />
                    </div>
                    <div className="h-16 flex items-center justify-center min-w-[120px] mr-6">
                      <img src="/Amazfit_logo_b.png" alt="Amazfit" className="max-h-11 w-auto object-contain" />
                    </div>
                    <div className="h-16 flex items-center justify-center min-w-[120px] mr-9">
                      <img src="/logo-honor-Photoroom.png" alt="HONOR" className="max-h-6 w-auto object-contain" />
                    </div>
                    <div className="h-16 flex items-center justify-center min-w-[120px] mr-0">
                      <img src="/doodledesk.png" alt="DoodleDesk" className="max-h-12 w-auto object-contain" />
                    </div>
                    <div className="h-16 flex items-center justify-center min-w-[120px] mr-8">
                      <img src="/DailyU.png" alt="DailyU" className="max-h-8 w-auto object-contain" />
                    </div>
                  </div>
                  <style>{`
                    .logo-marquee {
                      animation: marquee-left 18s linear infinite;
                    }
                    @keyframes marquee-left {
                      0% { transform: translateX(0); }
                      100% { transform: translateX(-50%); }
                    }
                    .scrollbar-hide::-webkit-scrollbar { display: none; }
                    .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
                  `}</style>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Scroll Indicator */}
        {/* <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-gray-400 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div> */}{" "}
        {/* Custom CSS for radial gradients */}
        <style jsx>{`
          .bg-gradient-radial {
            background: radial-gradient(
              circle at center,
              var(--tw-gradient-stops)
            );
          }
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fadeIn {
            animation: fadeIn 0.3s ease-out;
          }
        `}</style>
      </section>{" "}
      {/* About Section */}
      <section id="about" className="py-24 bg-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-32 left-1/4 w-72 h-72 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-40 animate-blob"></div>
          <div className="absolute bottom-32 right-1/4 w-72 h-72 bg-purple-100 rounded-full mix-blend-multiply filter blur-xl opacity-40 animate-blob animation-delay-2000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 text-sm font-medium mb-6">
              <SparklesIcon className="h-4 w-4 mr-2" />
              About Uttik
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What is{" "}
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
                Uttik?
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
              A no-code, plug-and-play AI Answer Engine that delivers fast, accurate, and on-brand answers—wherever your customers ask.
            </p>
          </div>

          {/* Main content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start mb-20">
            <div className="space-y-8">
              

              {/* Why Uttik section */}
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-3 ml-5">
                    <LightbulbIcon className="!text-white" fontSize="small" />
                  </div>
                  Why Uttik?
                </h3>
                <div className="space-y-4">
                  {[{
                    icon: BoltIcon,
                    title: "No Code. No Clutter.",
                    description: "Deploy in minutes—no developers, no delays."
                  },
                  {
                    icon: ChatBubbleLeftRightIcon,
                    title: "Smart Answers, Structured for Search.",
                    description: "AI-crafted, schema-rich replies optimized for Google, Gemini, and GPT interfaces."
                  },
                  {
                    icon: ChartBarIcon,
                    title: "On-Brand, At Scale.",
                    description: "Trained on your own content. Uttik speaks your tone—24x7."
                  },
                  {
                    icon: ShieldCheckIcon,
                    title: "Human-in-the-Loop Accuracy.",
                    description: "Escalates complex queries—ensuring trust is never compromised."
                  },
                  {
                    icon: LockClosedIcon,
                    title: "Enterprise-Ready.",
                    description: "Secure. Scalable. Multi-tenant. Privacy-first."
                  }].map((feature, index) => (
                    <div key={index} className="flex items-start space-x-4 p-4 rounded-xl hover:bg-gray-50 transition-colors">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <feature.icon className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">{feature.title}</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl p-8 border border-blue-100">
                <p className="text-lg text-gray-700 leading-relaxed">
                  Whether it's your website, Google, ChatGPT, Gemini, or any AI interface—Uttik ensures your brand speaks with{" "}
                  <span className="font-semibold text-blue-600">consistency</span>,{" "}
                  <span className="font-semibold text-purple-600">context</span>, and{" "}
                  <span className="font-semibold text-indigo-600">credibility</span>.
                </p>
              </div>
            </div>

            {/* Right side - Name origin and founder note */}
            <div className="space-y-8">
              {/* What's Behind the Name */}
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <HelpOutlineIcon className="!text-white" fontSize="small" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">What's Behind the Name?</h3>
                </div>
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-100">
                    <p className="text-lg font-semibold text-gray-900 mb-2">
                      Uttik = <span className="text-indigo-600">Uttar</span> (Answer) + <span className="text-purple-600">Quick</span>
                    </p>
                    <p className="text-gray-600 text-sm">
                      A name that blends timeless meaning with modern agility.
                    </p>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Because in the AI era, it's not just what you say—it's{" "}
                    <span className="font-semibold text-blue-600">how fast</span>,{" "}
                    <span className="font-semibold text-purple-600">how right</span>, and{" "}
                    <span className="font-semibold text-indigo-600">how relevant</span>.
                  </p>
                </div>
              </div>

              {/* Founder's Note */}
             
                <div className="bg-gray-800 text-white p-9 rounded-2xl shadow-lg relative overflow-hidden">
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-indigo-500/20 rounded-full"></div>
                  <div className="absolute -bottom-12 -left-12 w-40 h-40 bg-indigo-500/20 rounded-full"></div>
                  <div className="relative z-10">
                    <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3 -ml-2">
                      <ArchitectureIcon className="!text-white" fontSize="large" />
                      Founder's Note
                    </h2>
                    <blockquote className="border-l-4 border-indigo-400 pl-6 italic text-gray-300 space-y-4">
                      <p>"At Uttik, we believe the future of customer experience lies in accurate, instant answers—across every surface where your brand exists. But AI isn't magic. It needs structure, training, and human empathy."</p>
                      <p>"That's why we built Uttik—to make enterprise-grade AI adoption simple, fast, and trustworthy. With real humans in the loop and your content at its core, Uttik ensures your brand always says the right thing at the right time."</p>
                    </blockquote>
                    {/* <div className="flex items-center mt-8">
                      <div className="w-14 h-14 bg-indigo-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mr-4 -ml-2">
                        H
                      </div>
                      <div>
                        <p className="font-semibold text-lg text-white">Honey Singh</p>
                        <p className="text-indigo-300">Founder</p>
                      </div>
                    </div> */}
                  </div>
                </div>
              
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-blue-50 via-purple-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Ready to experience the power of Uttik?
              </h3>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Join thousands of businesses already using our AI Answer Engine to transform their customer experience.
              </p>
              {isSignedIn ? (
                <button 
                  onClick={() => navigate('/')}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center mx-auto"
                >
                  Start Your Free Trial
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </button>
              ) : (
                <SignInButton mode="modal" navigate="/">
                  <button onClick={() => localStorage.setItem('redirectAfterSignIn','/')} className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center mx-auto">
                    Start Your Free Trial
                    <ArrowRightIcon className="ml-2 h-5 w-5" />
                  </button>
                </SignInButton>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* Features Section */}
      <section
        id="features"
        className="py-24 bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-4">
              <SparklesIcon className="h-4 w-4 mr-2" />
              See It In Action
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Watch AI Transform Your
              <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Customer Experience
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how our intelligent widgets seamlessly integrate with your
              website and deliver instant, accurate support
            </p>{" "}
          </div>{" "}
          {/* Vertical Stepper Layout */}
          <div ref={stepperSectionRef} className="relative max-w-6xl mx-auto">
            {/* Static Background Line - Full height for reference */}
            <div className="absolute left-1/2 transform -translate-x-1/2 top-0 -bottom-28 w-1 bg-gray-200 rounded-full hidden lg:block"></div>

            {/* Animated Progress Line - Grows with scroll */}
            <div
              className="absolute left-1/2 transform -translate-x-1/2 top-0 w-1 bg-gradient-to-b from-blue-500 via-purple-500 to-indigo-500 rounded-full hidden lg:block transition-all duration-500 ease-out z-10 shadow-lg"
              style={{
                height: `${lineHeight}%`,
                maxHeight: "calc(100% + 7rem)", // Matches the -bottom-28 from original
                boxShadow:
                  lineHeight > 0 ? "0 0 20px rgba(99, 102, 241, 0.4)" : "none",
              }}
            ></div>

            {/* Step 1 - Upload */}
            <div ref={step1Ref} className="relative mb-16 lg:mb-24 group">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                {/* Left Side - Content */}
                <div className="lg:text-right lg:pr-12 order-1 ">
                  {" "}
                  {/* Step Badge */}
                  <div className="flex lg:justify-end justify-start mb-6">
                    <div className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full font-bold text-sm shadow-lg lg:absolute lg:left-1/2 lg:transform lg:-translate-x-1/2 lg:top-8 lg:z-20 lg:px-4 lg:py-2">
                      <span className="lg:hidden mr-2">Step</span>01 Upload
                    </div>
                  </div>
                  <h3 className="text-2xl lg:text-3xl max-lg:hidden font-bold text-gray-900 mb-4">
                    Upload Your Content
                  </h3>
                  <p className="text-lg text-gray-600 max-lg:hidden leading-relaxed mb-6 lg:mb-0">
                    Simply upload PDFs, paste text, or provide URLs. Our AI
                    analyzes and understands your content instantly, extracting
                    key insights to create comprehensive knowledge.
                  </p>
                </div>

                {/* Right Side - Visual Element */}
                <div className="lg:pl-12 order-2 ">
                  <div className="bg-white/70 backdrop-blur-lg rounded-2xl p-6 lg:p-8 shadow-xl border border-white/20 hover:shadow-2xl transition-all duration-300 group-hover:scale-105">
                    <div className="bg-gradient-to-r from-gray-100 to-gray-50 rounded-lg p-6 border-2 border-dashed border-gray-300">
                      <div className="text-center">
                        <div className="w-12 h-12 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                          <span className="text-blue-600 text-lg">📄</span>
                        </div>
                        <p className="text-gray-700 font-medium mb-2">
                          Drop files or paste content
                        </p>
                        <p className="text-sm text-gray-500">
                          PDF, Word, URLs, or plain text
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="lg:hidden lg:pr-12 order-1 ">
                  {" "}
                  {/* Step Badge */}
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                    Upload Your Content
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed mb-6 lg:mb-0">
                    Simply upload PDFs, paste text, or provide URLs. Our AI
                    analyzes and understands your content instantly, extracting
                    key insights to create comprehensive knowledge.
                  </p>
                </div>
              </div>
            </div>
            {/* Step 2 - AI Processing */}
            <div ref={step2Ref} className="relative mb-16 lg:mb-24 group">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                {" "}
                {/* Left Side - Visual Element */}
                <div className="lg:pr-12 order-2 lg:order-1">
                  <div className="bg-white/70 backdrop-blur-lg rounded-2xl p-6 lg:p-8 shadow-xl border border-white/20 hover:shadow-2xl transition-all duration-300 group-hover:scale-105">
                    <FAQAccordion
                      faqs={[
                        {
                          question: "How does pricing work?",
                          answer:
                            "Our pricing is transparent and flexible. Choose from three tiers: Starter ($99/month) with 3 months free trial for small teams, Professional ($249/month) with a 30-day free trial for growing companies (Most Popular), and Enterprise (Custom pricing) which includes a 14-day free trial plus a custom demo. Contact sales for enterprise options.",
                          gradient: "from-purple-50 to-blue-50",
                          borderColor: "border-purple-200",
                        },
                        {
                          question: "What features are included?",
                          answer:
                            "All plans include AI-powered FAQ generation, smart chat support, SEO optimization, analytics dashboard, and 24/7 availability. Higher tiers add unlimited widgets, advanced customization, API access, and priority support.",
                          gradient: "from-blue-50 to-indigo-50",
                          borderColor: "border-blue-200",
                        },
                        {
                          question: "How do I get started?",
                          answer:
                            "Getting started is simple! Sign up for your free trial, upload your content or paste URLs, let our AI generate your FAQs, and deploy with just one line of code. Setup takes less than 5 minutes.",
                          gradient: "from-indigo-50 to-purple-50",
                          borderColor: "border-indigo-200",
                        },
                      ]}
                    />
                  </div>
                </div>
                {/* Right Side - Content */}
                <div className="lg:pl-12  order-1 lg:order-2">
                  {" "}
                  {/* Step Badge */}
                  <div className="flex justify-start mb-6">
                    <div className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-full font-bold text-sm shadow-lg lg:absolute lg:left-1/2 lg:transform lg:-translate-x-1/2 lg:top-8 lg:z-20 lg:px-4 lg:py-2">
                      <span className="lg:hidden mr-2">Step</span>02 Generate
                    </div>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                    AI Generates Smart FAQs
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    Advanced AI automatically creates comprehensive,
                    context-aware FAQs with perfect accuracy in seconds. Each
                    question is crafted to address real customer needs and
                    concerns.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 3 - Deploy */}
            <div className="relative group">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                {/* Left Side - Content */}
                <div className="lg:text-right lg:pr-12 order-1">
                  {" "}
                  {/* Step Badge */}
                  <div className="flex lg:justify-end justify-start mb-6">
                    <div className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-indigo-500 to-blue-500 text-white rounded-full font-bold text-sm shadow-lg lg:absolute lg:left-1/2 lg:transform lg:-translate-x-1/2 lg:top-8 lg:z-20 lg:px-4 lg:py-2">
                      <span className="lg:hidden mr-2">Step</span>03 Deploy
                    </div>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                    Deploy & Watch Magic
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed mb-6 lg:mb-0">
                    Copy one line of code to add intelligent widgets to your
                    website. Works with any platform instantly and starts
                    improving SEO, AEO, GEO, and customer experience on any
                    platform.
                  </p>
                </div>

                {/* Right Side - Visual Element */}
                <div className="lg:pl-12 order-2">
                  <div className="bg-white/70 backdrop-blur-lg rounded-2xl p-6 lg:p-8 shadow-xl border border-white/20 hover:shadow-2xl transition-all duration-300 group-hover:scale-105">
                    <div className="bg-gray-900 rounded-lg p-6 text-green-400 font-mono">
                      <div className="text-sm mb-3 text-gray-400">
                        // Add to your website
                      </div>
                      <div className="mb-2 text-green-300 text-xs lg:text-sm">
                        &lt;script src="aeo-widget.js"&gt;
                      </div>
                      <div className="text-gray-500 text-sm">
                        // That's it! 🎉
                      </div>
                      <div className="mt-4 text-xs text-blue-400">
                        ✅ Works with any platform
                        <br />
                        ✅ No technical setup required
                        <br />✅ Instant customer support
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Large Demo Widget */}
          <div className="mt-32 relative w-full max-w-7xl mx-auto max-lg:hidden">
            {/* Background Browser Window - Behind everything */}
            <div className="absolute inset-0 top-8 z-0">
              <div className="bg-white/60 backdrop-blur-sm rounded-3xl shadow-xl border border-white/30 overflow-hidden h-full">
                {/* Browser Header */}
                <div className="bg-gray-100/80 px-6 py-4 border-b border-gray-200/60">
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 rounded-full bg-red-400/70"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-400/70"></div>
                      <div className="w-3 h-3 rounded-full bg-green-400/70"></div>
                    </div>
                    <div className="flex-1 mx-4">
                      <div className="bg-white/80 rounded-lg px-4 py-2 text-sm text-gray-600 text-center max-w-md mx-auto">
                        🌐 yourwebsite.com/support - Live Demo
                      </div>
                    </div>
                  </div>
                </div>
                {/* Browser Content Area */}
                <div className="p-8 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 h-full">
                  {/* Subtle background content */}
                  <div className="space-y-4 opacity-30">
                    <div className="h-6 bg-gray-300 rounded w-1/2"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                      <div className="h-4 bg-gray-200 rounded w-4/5"></div>
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Three-Section Grid Layout */}
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-10 gap-6 lg:gap-6 min-h-[600px] lg:min-h-[500px]">
              {/* Left Section - Smaller */}
              <div className="w-full max-w-xs mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden col-span-3 mt-32 ml-5 self-start">
                {/* Header */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                        <Zap className="w-3 h-3 text-white" />
                      </div>
                      <h3 className="font-semibold text-gray-800 text-sm">
                        FAQ Generator
                      </h3>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                      <span className="text-xs text-gray-500">Live</span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 space-y-3">
                  <div className="space-y-2">
                    <div className="flex items-start space-x-2 p-2 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5"></div>
                      <div className="flex-1">
                        <p className="text-xs font-medium text-gray-800">
                          How do I reset my password?
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Auto-generated • 2min ago
                        </p>
                      </div>
                      <Check className="w-3 h-3 text-green-500 mt-0.5" />
                    </div>

                    <div className="flex items-start space-x-2 p-2 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors cursor-pointer">
                      <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mt-1.5"></div>
                      <div className="flex-1">
                        <p className="text-xs font-medium text-gray-800">
                          What are your business hours?
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Auto-generated • 5min ago
                        </p>
                      </div>
                      <Check className="w-3 h-3 text-green-500 mt-0.5" />
                    </div>

                    <div className="flex items-start space-x-2 p-2 bg-pink-50 rounded-lg hover:bg-pink-100 transition-colors cursor-pointer">
                      <div className="w-1.5 h-1.5 bg-pink-500 rounded-full mt-1.5"></div>
                      <div className="flex-1">
                        <p className="text-xs font-medium text-gray-800">
                          How can I contact support?
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Auto-generated • 8min ago
                        </p>
                      </div>
                      <Clock className="w-3 h-3 text-orange-500 mt-0.5" />
                    </div>
                  </div>

                  <div className="pt-3 border-t border-gray-100">
                    <button className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white py-2 px-3 rounded-lg font-medium hover:from-blue-600 hover:to-purple-600 transition-all duration-200 flex items-center justify-center space-x-1 text-xs">
                      <Plus className="w-3 h-3" />
                      <span>Generate More FAQs</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Middle Section - Larger, Prominent Foreground */}
              <div className="lg:col-span-4 flex items-center justify-center ">
                <div className="w-full max-w-lg transform hover:scale-105 transition-transform duration-300">
                  <div className="bg-white/95 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 overflow-hidden relative z-20">
                    {/* Enhanced Widget Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                            <SparklesIconSolid className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <h3 className="font-bold text-white text-lg">
                              AI Support Assistant
                            </h3>
                            <p className="text-blue-100 text-sm">
                              Online • Instant answers
                            </p>
                          </div>
                        </div>
                        <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                      </div>
                    </div>

                    {/* Enhanced Chat Messages */}
                    <div className="p-6 space-y-4 max-h-80 overflow-y-auto bg-gradient-to-br from-blue-50/80 to-indigo-50/80">
                      {/* Welcome Message */}
                      <div className="flex justify-start">
                        <div className="bg-white/90 backdrop-blur-sm px-4 py-3 rounded-2xl rounded-tl-sm text-sm max-w-xs border border-white/40 shadow-lg">
                          <p className="mb-2">
                            👋 Hi! I'm your AI assistant. How can I help you
                            today?
                          </p>
                        </div>
                      </div>
                      {/* User Message */}
                      <div className="flex justify-end">
                        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-3 rounded-2xl rounded-tr-sm text-sm max-w-xs shadow-lg">
                          How does your AI actually work?
                        </div>
                      </div>
                      {/* AI Response */}
                      <div className="flex justify-start">
                        <div className="bg-white/90 backdrop-blur-sm px-4 py-3 rounded-2xl rounded-tl-sm text-sm max-w-xs border border-white/40 shadow-lg">
                          <p className="mb-2">
                            Great question! Our AI uses advanced natural
                            language processing to:
                          </p>
                          <ul className="text-xs space-y-1 text-gray-600">
                            <li>• Analyze your content instantly</li>
                            <li>• Generate contextual responses</li>
                            <li>• Learn from interactions ✨</li>
                          </ul>
                        </div>
                      </div>{" "}
                      {/* Quick Suggestions */}
                      <div className="pt-2">
                        <p className="text-xs text-gray-600 mb-3">
                          🚀 Popular questions:
                        </p>
                        <FAQAccordion
                          faqs={[
                            {
                              question: "💰 What are your pricing plans?",
                              answer:
                                "We offer three flexible plans: Starter at $99/month (3 months free trial) for small teams, Professional at $249/month (30-day free trial) — our Most Popular plan — and Enterprise at $599/month (14-day free trial + custom demo). All plans include core AI features.",
                              gradient: "from-white/60 to-white/80",
                              borderColor: "border-white/40",
                            },
                            {
                              question: "🔧 How do I integrate this?",
                              answer:
                                "Integration is incredibly simple! Just copy one line of JavaScript code and paste it into your website. Works with any platform - WordPress, Shopify, React, or plain HTML. No technical setup required!",
                              gradient: "from-white/60 to-white/80",
                              borderColor: "border-white/40",
                            },
                          ]}
                          className="space-y-2"
                        />
                      </div>
                    </div>

                    {/* Enhanced Input Area */}
                    <div className="p-2 border-t border-white/30 bg-white/40 backdrop-blur-sm">
                      <div className="flex items-center space-x-3">
                        <input
                          type="text"
                          placeholder="Ask me anything..."
                          className="flex-1 bg-white/80 backdrop-blur-sm border border-white/50 rounded-xl px-4 py-3 text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
                          disabled
                        />
                        <button className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-3 rounded-xl hover:shadow-lg transition-all shadow-sm">
                          <ArrowRightIcon className="h-4 w-4" />
                        </button>
                      </div>
                      {/* <p className="text-xs text-gray-500 mt-2 text-center">Powered by AEO Platform AI</p> */}
                    </div>
                  </div>
                </div>
              </div>
              {/* Right Section - Smaller */}
              <div className="w-full max-w-xs mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden col-span-3 mt-32 self-start">
                {/* Header */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                      <BarChart3 className="w-3 h-3 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-800 text-sm">
                      SEO Dashboard
                    </h3>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 space-y-4">
                  <div className="space-y-3">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-600">
                          Search Visibility
                        </span>
                        <span className="text-xs font-bold text-green-600">
                          +24%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-1000"
                          style={{ width: "78%" }}
                        ></div>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-600">
                          FAQ Rankings
                        </span>
                        <span className="text-xs font-bold text-green-600">
                          +31%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-1000"
                          style={{ width: "85%" }}
                        ></div>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-600">
                          Schema Markup
                        </span>
                        <span className="text-xs font-bold text-green-600">
                          Active
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-green-500 to-emerald-400 h-2 rounded-full transition-all duration-1000"
                          style={{ width: "100%" }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-xs">47</span>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-800">
                          FAQs Optimized
                        </p>
                        <p className="text-xs text-gray-600">This month</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="pricing" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="relative text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Simple, transparent pricing
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose the plan that's right for your business. All plans include
            our core AI features with a 14-day free trial.
          </p>

          {/* Currency Selector */}
          <div className="mt-6 md:mt-0 md:absolute md:top-20 md:right-0">
            <span className="block text-sm font-medium text-gray-700 mb-1 text-center md:text-right">
              Currency
            </span>
            <div className="relative inline-block">
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="appearance-none bg-white border border-gray-300 rounded-md pl-3 pr-10 py-2 text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {Object.keys(currencyRates).map((cur) => (
                  <option key={cur} value={cur}>
                    {cur}
                  </option>
                ))}
              </select>
              <svg
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 transform h-4 w-4 text-gray-400"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.08 1.04l-4.25 4.25a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pricing.map((plan, index) => (
            <div
              key={index}
              className={`relative rounded-lg shadow-sm ${
                plan.popular
                  ? "border-2 border-blue-500 shadow-lg"
                  : "border border-gray-200"
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {plan.name}
                </h3>
                <p className="text-gray-600 mb-6">{plan.description}</p>

                <div className="mb-4">
                  <div className="flex items-baseline justify-center md:justify-start gap-2">
                    {plan.price === "Custom" ? (
                      <span className="text-4xl font-bold text-gray-900">
                        {plan.price}
                      </span>
                    ) : (
                      <>
                        <span className="text-4xl font-bold text-gray-900">
                          {convertPrice(plan.price)}
                        </span>
                        <span className="text-gray-600">/month</span>
                      </>
                    )}
                  </div>
                  {plan.trial && (
                    <div className="text-sm text-gray-500 mt-2">
                      {plan.trial}
                    </div>
                  )}
                </div>

                {/* CTA Buttons */}
                {plan.cta === "Contact Sales" ? (
                  <a
                    href="mailto:honey@fastpr.co?subject=Enterprise%20Package%20Inquiry"
                    className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors text-center block ${
                      plan.popular
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                    }`}
                  >
                    {plan.cta}
                  </a>
                ) : isSignedIn ? (
                  <button
                    onClick={() => navigate("/billing")}
                    className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                      plan.popular
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                    }`}
                  >
                    {plan.cta}
                  </button>
                ) : (
                  <SignInButton mode="modal" redirectUrl="/billing">
                    <button
                      onClick={() =>
                        localStorage.setItem("redirectAfterSignIn", "/billing")
                      }
                      className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                        plan.popular
                          ? "bg-blue-600 text-white hover:bg-blue-700"
                          : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                      }`}
                    >
                      {plan.cta}
                    </button>
                  </SignInButton>
                )}

                {/* Features */}
                <ul className="mt-6 space-y-3">
                  {plan.features.map((feature, featureIndex) => (
                    <li
                      key={featureIndex}
                      className="flex items-start text-gray-600"
                    >
                      <CheckIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4">
            Need a custom solution? We offer enterprise packages with custom
            integrations.
          </p>
          <a
            href="mailto:honey@fastpr.co?subject=Enterprise%20Package%20Inquiry"
            className="text-blue-600 font-semibold hover:text-blue-700 transition-colors duration-200"
          >
            Contact Sales →
          </a>
        </div>
      </div>
    </section> 
     
      <FAQWidgetComponent />
      {/* Testimonials Section */}
      <section
        id="testimonials"
        className="py-24 bg-gradient-to-br from-gray-50 via-purple-50 to-blue-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 text-sm font-medium mb-4">
              <StarIconSolid className="h-4 w-4 mr-2" />
              Customer Love
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Loved by customers
              <span className="block bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                worldwide
              </span>
            </h2>
            <p className="text-xl text-gray-600">
              See what our customers are saying about transforming their support
              experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="group">
                <div className="bg-white/70 backdrop-blur-lg rounded-2xl p-8 shadow-xl border border-white/20 hover:shadow-2xl hover:scale-105 transition-all duration-300 h-full">
                  {/* Star Rating */}
                  <div className="flex items-center mb-6">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <StarIconSolid
                        key={i}
                        className="h-5 w-5 text-yellow-400"
                      />
                    ))}
                  </div>

                  {/* Quote */}
                  <blockquote className="text-gray-700 mb-6 text-lg leading-relaxed">
                    "{testimonial.content}"
                  </blockquote>

                  {/* Author */}
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mr-4">
                      <span className="text-white font-bold text-lg">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">
                        {testimonial.name}
                      </p>
                      <p className="text-sm text-gray-600">
                        {testimonial.role}
                      </p>
                      <p className="text-sm text-blue-600 font-medium">
                        {testimonial.company}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Social Proof */}
          <div className="mt-16 text-center">
            <div className="bg-white/70 backdrop-blur-lg rounded-2xl p-8 shadow-xl border border-white/20 max-w-4xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                <div className="text-center">
                  <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-2">
                    4.9/5
                  </div>
                  <div className="text-gray-600 text-sm">Customer Rating</div>
                  <div className="flex justify-center mt-1">
                    {[...Array(5)].map((_, i) => (
                      <StarIconSolid
                        key={i}
                        className="h-4 w-4 text-yellow-400"
                      />
                    ))}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                    150M+
                  </div>
                  <div className="text-gray-600 text-sm">
                    AI Responses Served
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent mb-2">
                    98%
                  </div>
                  <div className="text-gray-600 text-sm">Accuracy Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent mb-2">
                    24/7
                  </div>
                  <div className="text-gray-600 text-sm">AI Availability</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>{" "}
      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-60 h-60 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-white/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Ready to transform your
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-pink-300">
                customer support?
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-blue-100 mb-12 max-w-3xl mx-auto leading-relaxed">
              Join thousands of businesses using Uttik - AEO Platform to provide better,
              faster, and more intelligent customer support with AI.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
              {isSignedIn ? (
                <button onClick={() => navigate('/overview')} className="group bg-white text-blue-600 px-10 py-5 rounded-2xl text-xl font-bold hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center">
                  Start Your Free Trial
                  <ArrowRightIcon className="ml-3 h-6 w-6 group-hover:translate-x-1 transition-transform" />
                </button>
              ) : (
                <SignInButton mode="modal" navigate="/overview">
                  <button onClick={() => localStorage.setItem('redirectAfterSignIn','/overview')} className="group bg-white text-blue-600 px-10 py-5 rounded-2xl text-xl font-bold hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center">
                    Start Your Free Trial
                    <ArrowRightIcon className="ml-3 h-6 w-6 group-hover:translate-x-1 transition-transform" />
                  </button>
                </SignInButton>
              )}
              <button
                className="group border-2 border-white/30 text-white px-10 py-5 rounded-2xl text-xl font-bold hover:bg-white/10 hover:border-white transition-all duration-300 backdrop-blur-sm"
                onClick={() => setIsVideoModalOpen(true)}
              >
                <PlayIcon className="inline mr-3 h-6 w-6" />
                Watch Demo
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center items-center gap-8 text-blue-200 text-sm">
              <div className="flex items-center">
                <CheckIconSolid className="h-5 w-5 text-green-400 mr-2" />
                14-day free trial
              </div>
              <div className="flex items-center">
                <CheckIconSolid className="h-5 w-5 text-green-400 mr-2" />
                No credit card required
              </div>
              <div className="flex items-center">
                <CheckIconSolid className="h-5 w-5 text-green-400 mr-2" />
                Cancel anytime
              </div>
              <div className="flex items-center">
                <CheckIconSolid className="h-5 w-5 text-green-400 mr-2" />
                Setup in 5 minutes
              </div>
            </div>

            {/* Additional CTA Elements */}
            <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-white">
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <ClockIcon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Start Today</h3>
                <p className="text-blue-200 text-sm">
                  Get up and running in minutes with our simple setup process
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <SparklesIconSolid className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2">See Results</h3>
                <p className="text-blue-200 text-sm">
                  Experience immediate improvements in support efficiency
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <RocketLaunchIcon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Scale Up</h3>
                <p className="text-blue-200 text-sm">
                  Grow your business with AI that scales with your needs
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>{" "}
      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <SparklesIconSolid className="h-5 w-5 text-white" />
                </div>
                <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  AEO Platform
                </span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed mb-6">
                AI-powered customer support solutions that help businesses
                provide better, faster, and more intelligent support
                experiences.
              </p>
              <div className="flex space-x-4">
                <a
                  href="https://x.com/gogetwellai"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gray-700 transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="text-sm">𝕏</span>
                </a>
                <a
                  href="https://www.linkedin.com/company/gogetwellai/"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gray-700 transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="text-sm">in</span>
                </a>
                <a
                  href="https://mail.google.com/mail/?view=cm&fs=1&to=honey@fastpr.co&su=Enterprise%20Package%20Inquiry&body=Hi%20Team%2C%0A%0AI%20am%20interested%20in%20learning%20more%20about%20your%20enterprise%20packages%20and%20custom%20integrations.%20Please%20share%20the%20details.%0A%0ARegards%2C%0A%5BYour%20Name%5D"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gray-700 transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="text-sm">@</span>
                </a>
              </div>
            </div>

            {/* Product */}
            {/* Product */}
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-3 text-sm">
                <li>
                  <a
                    href="#features"
                    className="hover:text-white transition-colors"
                  >
                    Features
                  </a>
                </li>
                <li>
                  <a
                    href="#pricing"
                    className="hover:text-white transition-colors"
                  >
                    Pricing
                  </a>
                </li>
                {/* <li>
                  <a href="#" className="hover:text-white transition-colors">
                    API Documentation
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Integrations
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Changelog
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Roadmap
                  </a>
                </li> */}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-3 text-sm">
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    About Us
                  </a>
                </li>
                {/* <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Blog
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Careers
                  </a>
                </li> */}
                <li>
                  <a
                    href="#testimonials"
                    className="hover:text-white transition-colors"
                  >
                    Testimonials
                  </a>
                </li>
                <li>
                  <a
                    href="https://mail.google.com/mail/?view=cm&fs=1&to=honey@fastpr.co&su=Enterprise%20Package%20Inquiry&body=Hi%20Team%2C%0A%0AI%20am%20interested%20in%20learning%20more%20about%20your%20enterprise%20packages%20and%20custom%20integrations.%20Please%20share%20the%20details.%0A%0ARegards%2C%0A%5BYour%20Name%5D"
                    className="hover:text-white transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="text-sm">contact</span>
                  </a>
                </li>
                {/* <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Partners
                  </a>
                </li> */}
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-white font-semibold mb-4">Support</h4>
              <ul className="space-y-3 text-sm">
                {/* <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Help Center
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Community
                  </a>
                </li> */}
                <li>
                  <Link
                    to="/privacy"
                    className="hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="hover:text-white transition-colors"
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    to="/refund"
                    className="hover:text-white transition-colors"
                  >
                    Refund Policy
                  </Link>
                </li>
                {/* <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Security
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Status Page
                  </a>
                </li> */}
              </ul>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex items-center space-x-6 mb-4 md:mb-0">
                <p className="text-sm text-gray-400">
                  &copy; 2024 AEO Platform. All rights reserved.
                </p>
                
              </div>
             
            </div>
          </div>
        </div>
      </footer>
      <ChatWidgetComponent />
    </div>
  );
}

export default LandingPage;
